import { atom, computed } from 'nanostores';

import type { Size } from '../../types.js';
import type { createPlaylistStore } from './playlist.js';

export function createSizeStore<PlaylistItemMeta>(
  playlist: ReturnType<typeof createPlaylistStore<PlaylistItemMeta>>,
) {
  /**
   * 1:1 display size of the media
   */
  const $naturalSize = atom({
    height: 0,
    width: 0,
  });
  function setNaturalSize(size: Size) {
    $naturalSize.set({
      height: size.height,
      width: size.width,
    });
  }

  playlist.$currentPlaylistItem.listen((playlistItem, oldPlaylistItem) => {
    if (oldPlaylistItem?.id === playlistItem?.id) {
      return;
    }
    const naturalSize = playlistItem?.naturalSize;
    if (naturalSize) {
      setNaturalSize({
        height: naturalSize.height,
        width: naturalSize.width,
      });
    }
  });

  /**
   * Size of the container.
   */
  const $containerSize = atom({
    height: 0,
    width: 0,
  });

  function setContainerSize(size: Size) {
    $containerSize.set({
      height: size.height,
      width: size.width,
    });
  }
  /**
   * Display size of the media element.
   * i.e constrained by the container size
   * Calculates the aspect ratio of the naturalSize and returns the size that fits the container
   */
  const $contentSize = computed(
    [$naturalSize, $containerSize],
    (naturalSize, containerSize) => {
      const aspectRatio = naturalSize.width / naturalSize.height;
      const containerAspectRatio = containerSize.width / containerSize.height;

      if (aspectRatio < containerAspectRatio) {
        return {
          height: containerSize.height,
          width: containerSize.height * aspectRatio,
        };
      }

      return {
        height: containerSize.width / aspectRatio,
        width: containerSize.width,
      };
    },
  );
  return {
    $naturalSize,
    $containerSize,
    $contentSize,
    setNaturalSize,
    setContainerSize,
  };
}
